/**
 * MailDomainsManagement.tsx
 *
 * Admin view of every customer mail domain across the platform. Lists
 * customer_mail_domains rows with SES + DNS publishing status; drill into
 * one to see its mailboxes + aliases and run direct CRUD operations
 * (overrides what the customer can do).
 *
 * Backed by /api/admin/mail/*.
 */
import React, { useEffect, useState } from 'react';

interface CustomerMailDomain {
  id: string;
  customer_id: string;
  hosting_account_id: string;
  domain: string;
  mail_ct_domain_id: number | null;
  ses_identity_status: 'pending' | 'verifying' | 'verified' | 'failed';
  ses_verified_at: string | null;
  dkim_published_at: string | null;
  mx_published_at: string | null;
  spf_published_at: string | null;
  dmarc_published_at: string | null;
  webmail_a_published_at: string | null;
  webmail_cert_issued_at: string | null;
  webmail_vhost_installed_at: string | null;
  last_error: string | null;
  created_at: string;
  updated_at: string;
}

interface Mailbox {
  id: number;
  email: string;
  quota_mb: number;
  active: boolean;
  autoresponder_enabled: boolean;
  created_at: string;
}

interface Alias {
  id: number;
  source: string;
  destination: string;
  is_catchall: boolean;
  active: boolean;
}

const apiBase = process.env.REACT_APP_API_URL || '';
function authFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = localStorage.getItem('token');
  return fetch(`${apiBase}${path}`, {
    ...init,
    headers: {
      ...(init.headers || {}),
      'Authorization': `Bearer ${token}`,
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
    },
  });
}

const MailDomainsManagement: React.FC = () => {
  const [domains, setDomains] = useState<CustomerMailDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<CustomerMailDomain | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setErr('');
    try {
      const r = await authFetch('/api/admin/mail/customer-domains');
      const j = await r.json();
      if (j.success) setDomains(j.domains || []);
      else setErr(j.error || 'Failed to load mail domains');
    } catch (e: any) { setErr(e.message || 'Network error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const refresh = async (id: string) => {
    setBusy(`refresh-${id}`);
    try {
      await authFetch(`/api/admin/mail/customer-domains/${id}/refresh-status`, { method: 'POST', body: JSON.stringify({}) });
      await load();
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(null); }
  };

  const deprovision = async (d: CustomerMailDomain) => {
    if (!window.confirm(`Deprovision ${d.domain}? Mailboxes, aliases, and all stored mail will be permanently deleted. SES identity + DNS records remain.`)) return;
    setBusy(`del-${d.id}`);
    try {
      await authFetch(`/api/admin/mail/customer-domains/${d.id}`, { method: 'DELETE' });
      setSelected(null);
      await load();
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(null); }
  };

  const filtered = domains.filter(d => !search || d.domain.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-5">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Customer Mail Domains</h2>
            <p className="text-xs text-gray-500 mt-1">
              Every customer mail domain on the platform with its provisioning state.
              Click a row to drill into mailboxes + aliases.
            </p>
          </div>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search domain…"
            className="border border-gray-300 rounded px-3 py-1.5 text-sm w-64"
          />
        </div>

        {err && <div className="mb-3 bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">{err}</div>}

        {loading && domains.length === 0 ? (
          <div className="text-sm text-gray-500 py-6">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-sm text-gray-500 py-6">No customer mail domains.</div>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-gray-500 border-b border-gray-200">
                <th className="py-2 pr-3">Domain</th>
                <th className="py-2 pr-3">SES</th>
                <th className="py-2 pr-3">DKIM</th>
                <th className="py-2 pr-3">SPF</th>
                <th className="py-2 pr-3">DMARC</th>
                <th className="py-2 pr-3">MX</th>
                <th className="py-2 pr-3">Webmail</th>
                <th className="py-2 pr-3">Created</th>
                <th className="py-2 pr-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(d => (
                <tr key={d.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-2 pr-3">
                    <button onClick={() => setSelected(d)} className="font-medium text-teal-700 hover:underline">
                      {d.domain}
                    </button>
                    {d.last_error && <div className="text-xs text-amber-700 mt-0.5 line-clamp-1">{d.last_error}</div>}
                  </td>
                  <td className="py-2 pr-3"><SesPill status={d.ses_identity_status} /></td>
                  <td className="py-2 pr-3"><Check ok={!!d.dkim_published_at} /></td>
                  <td className="py-2 pr-3"><Check ok={!!d.spf_published_at} /></td>
                  <td className="py-2 pr-3"><Check ok={!!d.dmarc_published_at} /></td>
                  <td className="py-2 pr-3"><Check ok={!!d.mx_published_at} /></td>
                  <td className="py-2 pr-3"><Check ok={!!d.webmail_vhost_installed_at} /></td>
                  <td className="py-2 pr-3 text-xs text-gray-500">{new Date(d.created_at).toLocaleDateString()}</td>
                  <td className="py-2 pr-3 text-right space-x-2 whitespace-nowrap">
                    <button
                      onClick={() => refresh(d.id)}
                      disabled={busy === `refresh-${d.id}`}
                      className="text-xs text-teal-700 hover:underline disabled:opacity-60"
                    >
                      {busy === `refresh-${d.id}` ? '…' : 'Refresh'}
                    </button>
                    <button
                      onClick={() => deprovision(d)}
                      disabled={busy === `del-${d.id}`}
                      className="text-xs text-red-600 hover:underline disabled:opacity-60"
                    >
                      Deprovision
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <DomainDetail domain={selected} onClose={() => setSelected(null)} onError={setErr} />
      )}
    </div>
  );
};

// ============================================================================
// Status pills
// ============================================================================

const SesPill: React.FC<{ status: CustomerMailDomain['ses_identity_status'] }> = ({ status }) => {
  const styles: Record<string, string> = {
    verified: 'bg-green-100 text-green-800',
    verifying: 'bg-amber-100 text-amber-800',
    pending: 'bg-gray-100 text-gray-700',
    failed: 'bg-red-100 text-red-800',
  };
  return <span className={`text-xs px-2 py-0.5 rounded ${styles[status] || styles.pending}`}>{status}</span>;
};

const Check: React.FC<{ ok: boolean }> = ({ ok }) => (
  <span className={`text-xs px-2 py-0.5 rounded ${ok ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-500'}`}>
    {ok ? '✓' : '—'}
  </span>
);

// ============================================================================
// Drill-down: mailboxes + aliases for one domain
// ============================================================================

const DomainDetail: React.FC<{
  domain: CustomerMailDomain;
  onClose: () => void;
  onError: (m: string) => void;
}> = ({ domain, onClose, onError }) => {
  const [mailboxes, setMailboxes] = useState<Mailbox[]>([]);
  const [aliases, setAliases] = useState<Alias[]>([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!domain.mail_ct_domain_id) return;
    setLoading(true);
    try {
      const [mb, al] = await Promise.all([
        authFetch(`/api/admin/mail/domains/${domain.mail_ct_domain_id}/mailboxes`).then(r => r.json()),
        authFetch(`/api/admin/mail/domains/${domain.mail_ct_domain_id}/aliases`).then(r => r.json()),
      ]);
      if (mb.success) setMailboxes(mb.mailboxes || []);
      if (al.success) setAliases(al.aliases || []);
    } catch (e: any) { onError(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [domain.id]);

  const deleteMailbox = async (m: Mailbox) => {
    if (!window.confirm(`Delete mailbox ${m.email}? All messages will be permanently lost.`)) return;
    try {
      const r = await authFetch(`/api/admin/mail/mailboxes/${m.id}`, { method: 'DELETE' });
      const j = await r.json();
      if (j.success) await load();
      else onError(j.error || 'Delete failed');
    } catch (e: any) { onError(e.message); }
  };

  const deleteAlias = async (a: Alias) => {
    if (!window.confirm(`Delete alias ${a.source} → ${a.destination}?`)) return;
    try {
      const r = await authFetch(`/api/admin/mail/aliases/${a.id}`, { method: 'DELETE' });
      const j = await r.json();
      if (j.success) await load();
      else onError(j.error || 'Delete failed');
    } catch (e: any) { onError(e.message); }
  };

  return (
    <div className="bg-white rounded-lg shadow p-5">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{domain.domain}</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            customer_id <code>{domain.customer_id.slice(0, 8)}…</code> ·
            hosting_account_id <code>{domain.hosting_account_id.slice(0, 8)}…</code> ·
            mail_ct_id <code>{domain.mail_ct_domain_id ?? 'none'}</code>
          </p>
        </div>
        <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-800">Close</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <h4 className="font-semibold text-gray-900 mb-2 text-sm">Mailboxes</h4>
          {loading && mailboxes.length === 0 ? (
            <div className="text-sm text-gray-500">Loading…</div>
          ) : mailboxes.length === 0 ? (
            <div className="text-sm text-gray-500 py-2">No mailboxes.</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 border-b border-gray-200">
                  <th className="py-1 pr-2">Email</th>
                  <th className="py-1 pr-2">Quota</th>
                  <th className="py-1 pr-2">Active</th>
                  <th className="py-1 pr-2">A/R</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {mailboxes.map(m => (
                  <tr key={m.id} className="border-b border-gray-100">
                    <td className="py-1 pr-2">{m.email}</td>
                    <td className="py-1 pr-2 text-gray-600">{m.quota_mb}MB</td>
                    <td className="py-1 pr-2"><Check ok={m.active} /></td>
                    <td className="py-1 pr-2"><Check ok={m.autoresponder_enabled} /></td>
                    <td className="py-1 pr-2 text-right">
                      <button onClick={() => deleteMailbox(m)} className="text-xs text-red-600 hover:underline">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div>
          <h4 className="font-semibold text-gray-900 mb-2 text-sm">Aliases &amp; catch-all</h4>
          {aliases.length === 0 ? (
            <div className="text-sm text-gray-500 py-2">No aliases.</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 border-b border-gray-200">
                  <th className="py-1 pr-2">From</th>
                  <th className="py-1 pr-2">To</th>
                  <th className="py-1 pr-2">Type</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {aliases.map(a => (
                  <tr key={a.id} className="border-b border-gray-100">
                    <td className="py-1 pr-2">{a.source}</td>
                    <td className="py-1 pr-2">{a.destination}</td>
                    <td className="py-1 pr-2 text-xs">{a.is_catchall ? 'catch-all' : 'alias'}</td>
                    <td className="py-1 pr-2 text-right">
                      <button onClick={() => deleteAlias(a)} className="text-xs text-red-600 hover:underline">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="mt-5 border-t border-gray-200 pt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <Stat label="SES" value={domain.ses_identity_status} />
        <Stat label="DKIM published" value={domain.dkim_published_at ? new Date(domain.dkim_published_at).toLocaleString() : '—'} />
        <Stat label="SPF published" value={domain.spf_published_at ? new Date(domain.spf_published_at).toLocaleString() : '—'} />
        <Stat label="DMARC published" value={domain.dmarc_published_at ? new Date(domain.dmarc_published_at).toLocaleString() : '—'} />
        <Stat label="MX published" value={domain.mx_published_at ? new Date(domain.mx_published_at).toLocaleString() : '—'} />
        <Stat label="Webmail vhost" value={domain.webmail_vhost_installed_at ? new Date(domain.webmail_vhost_installed_at).toLocaleString() : '—'} />
        <Stat label="Created" value={new Date(domain.created_at).toLocaleString()} />
        <Stat label="Updated" value={new Date(domain.updated_at).toLocaleString()} />
      </div>

      {domain.last_error && (
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
          <strong>Last error:</strong> {domain.last_error}
        </div>
      )}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <div className="text-gray-500 uppercase tracking-wide text-[10px]">{label}</div>
    <div className="text-gray-900">{value}</div>
  </div>
);

export default MailDomainsManagement;
